/**
 * Groups pages into the per-BU sections the Páginas screen renders.
 *
 * @module Pages/BUs/Pages
 */

import type { Page } from '../Types/Pages.types';
import { compareTenantSlugs } from '@/Shared/brand/tenantOrder';

/** One BU's section: the tenant and its pages, with the counts shown in the header. */
export interface TenantPageGroup {
  tenantSlug: string;
  pages: Page[];
  /** e.g. "3 páginas". */
  countLabel: string;
  /** e.g. "2 ativas". */
  activeLabel: string;
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Groups by tenant slug, keeping tenants ordered by compareTenantSlugs and, inside each, the main page
 * first — it is the one that receives announcements with no page of their own, so it leads.
 * Remaining pages keep their incoming order.
 *
 * @param pages - Every page in scope, already filtered by the tenant filter.
 */
export function groupPagesByTenant(pages: Page[]): TenantPageGroup[] {
  const byTenant = new Map<string, Page[]>();

  for (const page of pages) {
    const existing = byTenant.get(page.tenantSlug);
    if (existing) {
      existing.push(page);
    } else {
      byTenant.set(page.tenantSlug, [page]);
    }
  }

  return [...byTenant.entries()]
    .sort(([a], [b]) => compareTenantSlugs(a, b))
    .map(([tenantSlug, tenantPages]) => {
      const ordered = [...tenantPages].sort((a, b) => Number(b.isMain) - Number(a.isMain));
      const activeCount = ordered.filter(page => page.isActive).length;

      return {
        tenantSlug,
        pages: ordered,
        countLabel: pluralize(ordered.length, 'página', 'páginas'),
        activeLabel: pluralize(activeCount, 'ativa', 'ativas'),
      };
    });
}
